<script setup>
import {onMounted, ref, reactive, computed, watch} from "vue";
import axios from "axios";
import {storeToRefs} from 'pinia';
import {useAuthStore} from '@/stores/auth.store.js';

const authStore = useAuthStore();
const {user} = storeToRefs(authStore);

const isAdmin = computed(() => user.value?.role?.name?.toLowerCase() === 'admin');

// Admins can switch between their own uploads and everyone's.
const scope = ref('mine');
const filters = reactive({ q: '', email: '', status: '', from: '', to: '' });
const page = ref(1);
const perPage = 50;

const uploads = ref([]);
const total = ref(0);
const loading = ref(false);
const error = ref('');

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage)));

// A pending row means the browser never reported back. Within the
// presigned URL's 1-hour lifetime it may still be uploading; after that
// it almost certainly didn't finish.
const PENDING_GRACE_MS = 60 * 60 * 1000;

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const all = isAdmin.value && scope.value === 'all';
    const params = { page: page.value, perPage };
    for (const k of ['q', 'status', 'from', 'to']) if (filters[k]) params[k] = filters[k];
    if (all && filters.email) params.email = filters.email;
    const res = await axios.get(
      import.meta.env.VITE_API_BACKEND_URL + (all ? '/api/admin/uploads' : '/api/uploads/mine'),
      { params, withCredentials: true }
    );
    uploads.value = res.data.uploads;
    total.value = res.data.total;
  } catch (e) {
    console.error(e);
    error.value = e?.response?.data?.error || 'Could not load the upload log.';
  } finally {
    loading.value = false;
  }
};

const search = () => { page.value = 1; load(); };
const resetFilters = () => {
  Object.assign(filters, { q: '', email: '', status: '', from: '', to: '' });
  search();
};
const goToPage = (p) => { page.value = p; load(); };

watch(scope, search);

const fileName = (key) => key.split('/').pop();
const folderOf = (key) => key.slice(0, key.lastIndexOf('/') + 1);
const folderLink = (key) => {
  const dir = folderOf(key);
  return dir ? `/?dir=${encodeURIComponent(dir)}` : '/';
};

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = Number(bytes), i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const statusOf = (u) => {
  if (u.status === 'complete') {
    return u.present === false
      ? { label: 'No longer in folder', cls: 'bg-secondary', title: 'Uploaded successfully, but has since been deleted or moved.' }
      : { label: 'Uploaded', cls: 'bg-success', title: 'Upload finished and verified in storage.' };
  }
  if (u.status === 'failed') {
    return { label: 'Failed', cls: 'bg-danger', title: 'The upload failed or was cancelled. Try uploading it again.' };
  }
  const age = Date.now() - new Date(u.requested_at).getTime();
  return age < PENDING_GRACE_MS
    ? { label: 'In progress', cls: 'bg-info text-dark', title: 'The upload was started recently and has not finished yet.' }
    : { label: 'Not confirmed', cls: 'bg-warning text-dark', title: 'The upload started but never reported finishing (for example, the browser tab was closed). Check the folder, and upload again if it is missing.' };
};

onMounted(load);
</script>

<template>
  <div class="upload-log">
    <div class="d-flex flex-row justify-content-between align-items-center mb-3">
      <h1 class="h3 m-0">{{ isAdmin && scope === 'all' ? 'All uploads' : 'My uploads' }}</h1>
      <div v-if="isAdmin" class="btn-group btn-group-sm" role="group" aria-label="Whose uploads">
        <button type="button" class="btn" :class="scope === 'mine' ? 'btn-primary' : 'btn-outline-primary'" @click="scope = 'mine'">Mine</button>
        <button type="button" class="btn" :class="scope === 'all' ? 'btn-primary' : 'btn-outline-primary'" @click="scope = 'all'">All users</button>
      </div>
    </div>

    <form class="row g-2 align-items-end mb-3" @submit.prevent="search">
      <div class="col-12 col-md">
        <label class="form-label small mb-1" for="ul-q">File or folder name</label>
        <input id="ul-q" v-model="filters.q" class="form-control" placeholder="e.g. 1083R1" />
      </div>
      <div v-if="isAdmin && scope === 'all'" class="col-12 col-md">
        <label class="form-label small mb-1" for="ul-email">Uploaded by</label>
        <input id="ul-email" v-model="filters.email" class="form-control" placeholder="email" />
      </div>
      <div class="col-6 col-md-auto">
        <label class="form-label small mb-1" for="ul-from">From</label>
        <input id="ul-from" v-model="filters.from" type="date" class="form-control" />
      </div>
      <div class="col-6 col-md-auto">
        <label class="form-label small mb-1" for="ul-to">To</label>
        <input id="ul-to" v-model="filters.to" type="date" class="form-control" />
      </div>
      <div class="col-12 col-md-auto">
        <label class="form-label small mb-1" for="ul-status">Status</label>
        <select id="ul-status" v-model="filters.status" class="form-select">
          <option value="">Any</option>
          <option value="complete">Uploaded</option>
          <option value="failed">Failed</option>
          <option value="pending">Not confirmed</option>
        </select>
      </div>
      <div class="col-12 col-md-auto d-flex gap-2">
        <button type="submit" class="btn btn-primary">Search</button>
        <button type="button" class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
      </div>
    </form>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else-if="loading" class="text-muted">Loading…</div>

    <template v-else>
      <div class="table-responsive">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>File</th>
              <th>Folder</th>
              <th v-if="isAdmin && scope === 'all'">Uploaded by</th>
              <th class="text-end">Size</th>
              <th>When</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in uploads" :key="u.id">
              <td class="text-break">{{ fileName(u.key) }}</td>
              <td class="text-break">
                <a :href="folderLink(u.key)" title="Open this folder in the file manager"><code>{{ folderOf(u.key) || '/' }}</code></a>
              </td>
              <td v-if="isAdmin && scope === 'all'" class="small">{{ u.user_email }}</td>
              <td class="text-end text-nowrap">{{ formatSize(u.size) }}</td>
              <td class="text-nowrap small">{{ formatTime(u.uploaded_at || u.requested_at) }}</td>
              <td>
                <span class="badge" :class="statusOf(u).cls" :title="statusOf(u).title">{{ statusOf(u).label }}</span>
              </td>
            </tr>
            <tr v-if="!uploads.length">
              <td :colspan="isAdmin && scope === 'all' ? 6 : 5" class="text-muted text-center py-4">
                No uploads found. Only uploads made since the upload log was added are listed here.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="total > perPage" class="d-flex justify-content-between align-items-center">
        <span class="text-muted small">{{ total }} uploads · page {{ page }} of {{ totalPages }}</span>
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-secondary" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <button class="btn btn-outline-secondary" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>
    </template>
  </div>
</template>
